import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toLocalISODate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Bell, 
  Calendar, 
  Clock, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Palmtree,
  FileText,
  Trash2,
  CheckCheck,
  Filter,
  Download,
  BellRing,
  BellOff
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Notifications = () => {
  const { user, isAdmin, currentOrganization } = useAuth();
  const { language } = useLanguage();
  
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, unread, shift, vacation, incident
  const [unreadCount, setUnreadCount] = useState(0);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [exporting, setExporting] = useState(false);

  // Reset data when organization changes
  useEffect(() => {
    setNotifications([]);
    setEmployees([]);
    setSelectedEmployee('');
    setUnreadCount(0);
    setLoading(true);
  }, [currentOrganization?.id]);

  // Check if push notifications are supported
  useEffect(() => {
    if ('Notification' in window && 'serviceWorker' in navigator) {
      setPushSupported(true);
      setPushEnabled(Notification.permission === 'granted');
    }
  }, []);

  // Fetch employees for admin export
  useEffect(() => {
    if (isAdmin && isAdmin()) {
      fetchEmployees();
    }
  }, [isAdmin, currentOrganization?.id]);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/employees`);
      setEmployees((response.data || []).filter(e => e.is_active !== false));
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filter !== 'all') {
        if (filter === 'unread') {
          params.unread_only = true;
        } else {
          params.notification_type = filter;
        }
      }
      const response = await axios.get(`${API_URL}/api/notifications`, { params });
      setNotifications(response.data);
      setUnreadCount(response.data.filter(n => !n.read).length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Enable push notifications
  const enablePushNotifications = async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setPushEnabled(true);
        toast.success(language === 'es' ? 'Notificaciones push activadas' : 'Push notifications enabled');

        // Show a test notification
        new Notification(language === 'es' ? 'Notificaciones activadas' : 'Notifications enabled', {
          body: language === 'es' ? 'Recibirás alertas cuando haya nuevas notificaciones' : 'You will receive alerts for new notifications',
          icon: '/favicon.ico'
        });
      } else {
        toast.error(language === 'es' ? 'Permiso denegado' : 'Permission denied');
      }
    } catch (error) {
      toast.error(language === 'es' ? 'Error al activar notificaciones' : 'Error enabling notifications');
    }
  };

  // Browsers never let a website revoke its own notification permission - once
  // granted, only the browser's own site settings can turn it off. Clicking the
  // button while enabled just explains that, instead of doing nothing silently.
  const handlePushButtonClick = () => {
    if (pushEnabled) {
      toast.info(
        language === 'es'
          ? 'Por seguridad, los navegadores no permiten que una web desactive sus propias notificaciones. Ábrelas desde el candado o el icono ⓘ junto a la URL → Notificaciones, y bloquéalas ahí.'
          : "For security, browsers don't let a website turn off its own notifications. Open the padlock or ⓘ icon next to the URL → Notifications, and block them there.",
        { duration: 9000 }
      );
      return;
    }
    if (Notification.permission === 'denied') {
      toast.info(
        language === 'es'
          ? 'Las bloqueaste antes en el navegador. Ábrelas desde el candado o el icono ⓘ junto a la URL → Notificaciones, y permítelas para volver a activarlas.'
          : 'You blocked them earlier in the browser. Open the padlock or ⓘ icon next to the URL → Notifications, and allow them to turn this back on.',
        { duration: 9000 }
      );
      return;
    }
    enablePushNotifications();
  };

  // Export notifications
  const handleExport = async (format) => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      params.append('format', format);
      if (selectedEmployee && selectedEmployee !== 'all') {
        params.append('employee_id', selectedEmployee);
      }
      
      const response = await axios.get(`${API_URL}/api/notifications/export?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const employeeLabel = (selectedEmployee && selectedEmployee !== 'all') ? selectedEmployee : 'todos';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `notificaciones_${employeeLabel}_${toLocalISODate(new Date())}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(language === 'es' ? 'Exportación completada' : 'Export completed');
    } catch (error) {
      toast.error(language === 'es' ? 'Error al exportar' : 'Export error');
    } finally {
      setExporting(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API_URL}/api/notifications/${notificationId}/read`);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      toast.error(language === 'es' ? 'Error al marcar como leída' : 'Error marking as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API_URL}/api/notifications/mark-all-read`);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success(language === 'es' ? 'Todas marcadas como leídas' : 'All marked as read');
    } catch (error) {
      toast.error(language === 'es' ? 'Error' : 'Error');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`${API_URL}/api/notifications/${notificationId}`);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success(language === 'es' ? 'Notificación eliminada' : 'Notification deleted');
    } catch (error) {
      toast.error(language === 'es' ? 'Error al eliminar' : 'Error deleting');
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'shift_assigned':
      case 'shift_modified':
      case 'shift_deleted':
        return <Calendar className="w-5 h-5 text-indigo-500" />;
      case 'vacation_assigned':
      case 'vacation_approved':
      case 'vacation_rejected':
        return <Palmtree className="w-5 h-5 text-green-500" />;
      case 'incident_approved':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'incident_rejected':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'incident_created':
      case 'missed_clockin':
      case 'missed_clockin_supervisor':
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      default:
        return <Bell className="w-5 h-5 text-slate-500" />;
    }
  };

  const getNotificationColor = (type) => {
    if (type.startsWith('missed_clockin')) return 'bg-amber-50 border-amber-200';
    if (type.includes('approved') || type.includes('assigned')) return 'bg-green-50 border-green-200';
    if (type.includes('rejected') || type.includes('deleted')) return 'bg-red-50 border-red-200';
    if (type.includes('modified')) return 'bg-amber-50 border-amber-200';
    return 'bg-slate-50 border-slate-200';
  };

  const getTypeLabel = (type) => {
    const labels = {
      shift_assigned: language === 'es' ? 'Turno asignado' : 'Shift assigned',
      shift_modified: language === 'es' ? 'Turno modificado' : 'Shift modified',
      shift_deleted: language === 'es' ? 'Turno eliminado' : 'Shift deleted',
      vacation_assigned: language === 'es' ? 'Vacaciones asignadas' : 'Vacation assigned',
      vacation_approved: language === 'es' ? 'Vacaciones aprobadas' : 'Vacation approved',
      vacation_rejected: language === 'es' ? 'Vacaciones rechazadas' : 'Vacation rejected',
      incident_created: language === 'es' ? 'Incidencia creada' : 'Incident created',
      incident_approved: language === 'es' ? 'Incidencia aprobada' : 'Incident approved',
      incident_rejected: language === 'es' ? 'Incidencia rechazada' : 'Incident rejected',
      missed_clockin: language === 'es' ? 'No fichaste tu entrada' : "You didn't clock in",
      missed_clockin_supervisor: language === 'es' ? 'Empleado sin fichar' : 'Employee missed clock-in',
    };
    return labels[type] || type;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return language === 'es' ? 'Ahora mismo' : 'Just now';
    if (diffMins < 60) return `${diffMins} ${language === 'es' ? 'min' : 'min'}`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    
    return date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
      day: 'numeric',
      month: 'short'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notifications-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Bell className="w-8 h-8 text-indigo-600" />
          <div>
            <h1 className="text-2xl font-bold font-['Manrope'] text-slate-800">
              {language === 'es' ? 'Notificaciones' : 'Notifications'}
            </h1>
            {unreadCount > 0 && (
              <p className="text-sm text-slate-500">
                {unreadCount} {language === 'es' ? 'sin leer' : 'unread'}
              </p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[180px]" data-testid="notification-filter">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{language === 'es' ? 'Todas' : 'All'}</SelectItem>
              <SelectItem value="unread">{language === 'es' ? 'Sin leer' : 'Unread'}</SelectItem>
              <SelectItem value="shift">{language === 'es' ? 'Turnos' : 'Shifts'}</SelectItem>
              <SelectItem value="vacation">{language === 'es' ? 'Vacaciones' : 'Vacations'}</SelectItem>
              <SelectItem value="incident">{language === 'es' ? 'Incidencias' : 'Incidents'}</SelectItem>
            </SelectContent>
          </Select>
          
          {unreadCount > 0 && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={markAllAsRead}
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="w-4 h-4 mr-2" />
              {language === 'es' ? 'Marcar todas leídas' : 'Mark all read'}
            </Button>
          )}
        </div>
      </div>

      {/* Push Notifications & Export Section */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Push Notification Toggle */}
        {pushSupported && (
          <Button
            variant={pushEnabled ? "default" : "outline"}
            size="sm"
            onClick={handlePushButtonClick}
            title={pushEnabled ? (language === 'es' ? 'Gestionadas desde los ajustes del navegador' : 'Managed from your browser settings') : ''}
            data-testid="push-notifications-btn"
            className={pushEnabled ? "bg-green-600 hover:bg-green-700" : ""}
          >
            {pushEnabled ? (
              <>
                <BellRing className="w-4 h-4 mr-2" />
                {language === 'es' ? 'Push activadas' : 'Push enabled'}
              </>
            ) : (
              <>
                <BellOff className="w-4 h-4 mr-2" />
                {language === 'es' ? 'Activar notificaciones push' : 'Enable push notifications'}
              </>
            )}
          </Button>
        )}

        {/* Export Section (Admin only) */}
        {isAdmin && isAdmin() && (
          <div className="flex items-center gap-2 ml-auto">
            <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
              <SelectTrigger className="w-[200px]" data-testid="export-employee-select">
                <SelectValue placeholder={language === 'es' ? 'Todos los empleados' : 'All employees'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{language === 'es' ? 'Todos los empleados' : 'All employees'}</SelectItem>
                {employees.map(emp => (
                  <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport('xlsx')}
              disabled={exporting}
              data-testid="export-xlsx-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Excel
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport('pdf')}
              disabled={exporting}
              data-testid="export-pdf-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              PDF
            </Button>
          </div>
        )}
      </div>

      {/* Notifications List */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {notifications.length === 0 ? (
            <div className="p-8 text-center">
              <Bell className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">
                {language === 'es' ? 'No hay notificaciones' : 'No notifications'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 flex items-start gap-4 transition-colors hover:bg-slate-50 ${
                    !notification.read ? 'bg-indigo-50/30' : ''
                  }`}
                  data-testid={`notification-${notification.id}`}
                >
                  {/* Icon */}
                  <div className={`p-2 rounded-full ${getNotificationColor(notification.notification_type)}`}>
                    {getNotificationIcon(notification.notification_type)}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-800">
                            {notification.title}
                          </span>
                          {!notification.read && (
                            <Badge className="bg-indigo-500 text-white text-[10px]">
                              {language === 'es' ? 'Nueva' : 'New'}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 mt-1">
                          {notification.message}
                        </p>
                        {notification.details && (
                          <div className="mt-2 p-2 bg-slate-100 rounded text-xs text-slate-600">
                            {notification.details.date && (
                              <span className="mr-3">📅 {notification.details.date}</span>
                            )}
                            {notification.details.time && (
                              <span className="mr-3">🕐 {notification.details.time}</span>
                            )}
                            {notification.details.shift_name && (
                              <span>📋 {notification.details.shift_name}</span>
                            )}
                          </div>
                        )}
                      </div>
                      <span className="text-xs text-slate-400 whitespace-nowrap">
                        {formatDate(notification.created_at)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-1">
                    {!notification.read && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => markAsRead(notification.id)}
                        title={language === 'es' ? 'Marcar como leída' : 'Mark as read'}
                      >
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteNotification(notification.id)}
                      title={language === 'es' ? 'Eliminar' : 'Delete'}
                    >
                      <Trash2 className="w-4 h-4 text-red-400" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Notifications;
